// Admin, visão geral (croqui `screens/admin/visao-geral.tsx`, `spec/17` §8.2). Foco: **o
// número de pendências da fila**.
//
// Três leituras: `GET /admin/v1/overview` (os agregados, com `period`),
// `GET /admin/v1/system/alerts` (os críticos que entram na soma da fila) e
// `GET /admin/v1/settings/payouts-paused` (o aviso do topo). Nenhuma exige step-up -
// `ADMIN_STEP_UP_ROUTES` (`apps/api/src/admin-app.ts`) não as lista.
//
// Erro e skeleton do overview são resolvidos uma vez, no `QueueFocus`: o bloco de baixo
// vem do MESMO GET e repetir o `ErrorState` só faria a tela gritar duas vezes.

import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  adminAlertsQueryOptions,
  adminOverviewQueryOptions,
} from '../api/queries/admin-overview.ts';
import { PageHeader } from '../components/kit/page-header.tsx';
import {
  countFiring,
  resolvePeriod,
  validateAdminOverviewSearch,
} from '../features/admin-overview/model.ts';
import { PayoutsPausedNotice } from '../features/admin-overview/payouts-paused.tsx';
import { PeriodFilter } from '../features/admin-overview/period-filter.tsx';
import { payoutsPausedQueryOptions } from '../features/admin-overview/queries.ts';
import { QueueFocus } from '../features/admin-overview/queue-focus.tsx';
import { OverviewSummary, OverviewSummarySkeleton } from '../features/admin-overview/summary.tsx';

function AdminOverview() {
  const period = resolvePeriod(Route.useSearch());
  const navigate = Route.useNavigate();
  const overview = useQuery(adminOverviewQueryOptions(period));
  const alerts = useQuery(adminAlertsQueryOptions());
  const payouts = useQuery(payoutsPausedQueryOptions());

  const criticalAlerts =
    alerts.data === undefined ? undefined : countFiring(alerts.data.data, 'critical');

  return (
    <>
      <PageHeader
        title="Visão geral"
        sub="A fila da operação agora"
        path="/admin/v1/overview"
        size="lg"
        actions={
          <PeriodFilter
            value={period}
            onChange={(next) => {
              void navigate({ search: { period: next }, replace: true });
            }}
          />
        }
      />

      <PayoutsPausedNotice state={payouts.data} />

      <QueueFocus
        overview={overview.data}
        criticalAlerts={criticalAlerts}
        isPending={overview.isPending}
        error={overview.error}
        onRetry={() => {
          void overview.refetch();
        }}
      />

      {overview.isError ? null : overview.data === undefined ? (
        <OverviewSummarySkeleton />
      ) : (
        <OverviewSummary
          overview={overview.data}
          period={period}
          updatedAt={overview.dataUpdatedAt}
        />
      )}
    </>
  );
}

export const Route = createFileRoute('/admin/')({
  component: AdminOverview,
  validateSearch: validateAdminOverviewSearch,
});
